import { _x } from '@wordpress/i18n'
import { t } from '../../../../assets/utils/algolia/algoliaLocalization'
import { TranslatedHighlight } from '../../../../assets/utils/algolia/algoliaTranslation'
import type { HitComponentTemplateProps } from './HitComponent'
import useReplaceImgixUrl from '../../../../assets/utils/useReplaceImgixUrl'
import { useConfigContext } from './ConfigContext'

function CardHitComponent({
  hit,
  isNew,
  isGlobal,
  isRegional,
  regionalOrganisationName,
  isPermanent,
  isCancelled,
  tags,
  location,
  date,
  time,
  detailUrl,
  detailLinkProps,
  ctaButtonText,
  ctaButtonUrl,
  ctaLinkProps,
  teaser,
}: HitComponentTemplateProps) {
  const { imgixSrcUrl, imgixTargetUrl, typePreFilter } = useConfigContext()
  // On lists that are pre-filtered to offers only, every card is an offer, so the
  // "Ongoing Offer" badge is redundant and just covers the thumbnail — hide it there.
  const showPermanentBadge = isPermanent && typePreFilter !== 'offers'
  const imageUrl = t(hit.image?.url, { fallback: 'any' })
  const replaceImgixUrl = useReplaceImgixUrl(imgixSrcUrl, imgixTargetUrl)
  const imgixUrl = replaceImgixUrl(imageUrl, { width: 520 })

  return (
    <article
      data-bs-theme="dark"
      className="card h-100"
      style={{
        viewTransitionName: `card-${hit.objectID}`
      }}
    >
      <div className="card-tags card-tags--float">
        {isNew && (
          <div className="badge text-bg-primary">
            {_x('NEW', 'New Tag', 'icf')}
          </div>
        )}
        {isGlobal && (
          <div className="badge text-bg-primary2">
            {_x('ICF Movement', 'ICF Movement Tag', 'icf')}
          </div>
        )}
        {isRegional && (
          <div className="badge text-bg-trend3">
            {regionalOrganisationName}
          </div>
        )}
        {isCancelled && (
          <div className="badge text-bg-danger">
            {_x('Cancelled', 'Event Occurrence Cancelled Badge', 'icf')}
          </div>
        )}
        {showPermanentBadge && (
          <div className="badge d-inline-flex text-bg-black align-items-center gap-1">
            <i className="icon fa-rotate" />
            {_x('Ongoing Offer', 'Event Tag', 'icf')}
          </div>
        )}
        {tags.map((tag, idx) => (
          <div
            key={`${tag}_${
              // biome-ignore lint/suspicious/noArrayIndexKey: Don't care
              idx
            }`}
            className="badge text-bg-black"
          >
            {tag}
          </div>
        ))}
      </div>

      {imageUrl ? (
        <div className="card-img-top">
          <img
            src={imgixUrl}
            alt={t(hit.image.altText, { fallback: 'any' })}
            className="card-img-top"
            draggable={false}
            loading="lazy"
          />
        </div>
      ) : (
        <div className="card-img-top-placeholder" />
      )}
      <div className="card-body">
        <div className="card-meta">
          {!!date && !isPermanent && (
            <div className="card-meta-tag">
              <i className="icon fa-calendar-days" />
              <span className={isCancelled ? 'text-decoration-line-through' : undefined}>{date}</span>
            </div>
          )}
          {!!time && !isPermanent && (
            <div className="card-meta-tag">
              <i className="icon fa-clock" />
              <span className={isCancelled ? 'text-decoration-line-through' : undefined}>{time}</span>
            </div>
          )}
          {!!location && (
            <div className="card-meta-tag">
              <i className="icon fa-location-dot" />
              {location}
            </div>
          )}
        </div>
        <h3 className="card-title truncate-lines-2 mt-3">
          <TranslatedHighlight hit={hit} attribute="title" />
        </h3>
        <p className="card-text truncate-lines-2">
          {teaser}
        </p>
        <div className="card-buttons flex-column w-fit-content">
          <a
            className="btn btn-sm btn-dark-1 card-stretched-link"
            href={detailUrl}
            {...detailLinkProps}
            aria-label={`${_x('Details of event', 'Events List Card Detail Link Screenreader Text', 'icf')} ${t(hit.title, { fallback: 'none' })}`}
          >
            <span>
              {_x('Learn more', 'Events List Card Detail Link Button', 'icf')}
            </span>
            <i className="icon fs-base-const fa-arrow-right ms-auto" />
          </a>
          {!!(ctaButtonText && ctaButtonUrl) && (
            <a href={ctaButtonUrl} {...ctaLinkProps} className="btn btn-sm btn-dark-1 z-1">
              <span>{ctaButtonText}</span>
              <i className="icon fs-base-const fa-arrow-right ms-auto" />
            </a>
          )}
        </div>
      </div>
    </article>
  )
}

export default CardHitComponent
